import { Children, cloneElement, isValidElement, useMemo, useState } from 'react';
import { asset } from '../assets';
import { useLanguage } from './LanguageProvider';

const categoryOrder = [
  'business',
  'stationery',
];

const categoryLabels = {
  business: ['Business & Marketing', 'الأعمال والتسويق'],
  stationery: ['Stationery', 'القرطاسية'],
  packaging: ['Packaging & Bags', 'التغليف والأكياس'],
  stickers: ['Stickers & Labels', 'الملصقات والبطاقات'],
  books: ['Books & Publications', 'الكتب والمنشورات'],
  calendars: ['Calendars', 'التقويمات'],
  branding: ['Branding & Promo', 'العلامات التجارية والدعاية'],
  displays: ['Displays & Signage', 'العروض واللافتات'],
};

function elementSearchText(element) {
  if (!isValidElement(element)) return typeof element === 'string' ? element : '';

  return [
    element.props['data-en'],
    element.props['data-ar'],
    Children.toArray(element.props.children).map(elementSearchText).join(' '),
  ].filter(Boolean).join(' ');
}

function productEntry(element) {
  const card = element.props.children;
  return {
    category: card?.props?.['data-category'] || 'other',
    element,
    searchText: elementSearchText(card).toLocaleLowerCase(),
  };
}

export function filterProductEntries(entries, search, categories) {
  const query = search.trim().toLocaleLowerCase();
  return entries.filter((entry) => (
    (categories.length === 0 || categories.includes(entry.category))
    && (!query || entry.searchText.includes(query))
  ));
}

export function ProductBrowser({ children }) {
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const entries = useMemo(() => Children.toArray(children).map(productEntry), [children]);
  const visibleEntries = filterProductEntries(entries, search, categories);
  const visibleCategories = categoryOrder.filter((name) => visibleEntries.some((entry) => entry.category === name));

  const toggleCategory = (name) => {
    setCategories((current) => (
      current.includes(name)
        ? current.filter((category) => category !== name)
        : [...current, name]
    ));
  };

  return (
    <>
      <div className="product-toolbar">
        <label className="product-search">
          <span>{t('Search products', 'ابحث عن المنتجات')}</span>
          <div>
            <span aria-hidden="true" className="product-search-icon">
              <img src={asset('assets/search.svg')} alt="" />
            </span>
            <input
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('Search by product name...', 'ابحث باسم المنتج...')}
              type="search"
              value={search}
            />
          </div>
        </label>
        <fieldset className="product-category-select">
          <span>{t('Filter by category', 'تصفية حسب الفئة')}</span>
          <div className="product-category-options">
            <button
              aria-pressed={categories.length === 0}
              className={categories.length === 0 ? 'active' : ''}
              onClick={() => setCategories([])}
              type="button"
            >
              {t('All Products', 'جميع المنتجات')}
            </button>
            {categoryOrder.map((name) => (
              <button
                aria-pressed={categories.includes(name)}
                className={categories.includes(name) ? 'active' : ''}
                key={name}
                onClick={() => toggleCategory(name)}
                type="button"
              >
                {t(...categoryLabels[name])}
              </button>
            ))}
          </div>
        </fieldset>
      </div>

      <div className="product-category-list">
        {visibleCategories.map((name) => (
          <section className="product-category-group" key={name}>
            <div className="product-category-heading">
              <h3>{t(...categoryLabels[name])}</h3>
              <span>{visibleEntries.filter((entry) => entry.category === name).length}</span>
            </div>
            <div aria-label={t(...categoryLabels[name])} className="product-category-track">
              {visibleEntries
                .filter((entry) => entry.category === name)
                .map((entry) => cloneElement(entry.element, {
                  className: 'product-card-link',
                  key: entry.element.key,
                }))}
            </div>
          </section>
        ))}
        {visibleCategories.length === 0 && (
          <p className="product-empty">{t('No products match your search.', 'لا توجد منتجات تطابق بحثك.')}</p>
        )}
      </div>
    </>
  );
}
